/**
 * LoginPage
 * Organization login with email/password or Google
 */

import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  signIn,
  signInWithGoogle,
  signOut,
  resetPassword,
} from '@/services/auth.service';
import {
  hasActiveAccess,
  createOrganization,
  checkOrganizationExists,
} from '@/services/organization.service';
import { registerOrganization, SignupError } from '@/services/signup';
import {
  getAuthErrorCode,
  getAuthErrorMessage,
} from '@/utils/authErrors';
import './styles/LoginPage.css';

export function LoginPage() {
  const location = useLocation();
  const [isSignup, setIsSignup] = useState(location.state?.signup === true);
  const [orgName, setOrgName] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOrgNameModal, setShowOrgNameModal] = useState(false);
  // Authenticated user (Google or email) whose organization setup is unfinished
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [modalOrgName, setModalOrgName] = useState('');
  const [modalAdminPin, setModalAdminPin] = useState('');
  const navigate = useNavigate();

  const enterApp = (hasAccess: boolean) => {
    navigate(hasAccess ? '/booth' : '/billing');
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setResetMessage('');
    setEmail('');
    setPassword('');
    setOrgName('');
    setAdminPin('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setLoading(true);

    try {
      if (isSignup) {
        // HTML `required` lets whitespace-only names through
        if (!orgName.trim()) {
          setError('Please enter your organization name.');
          setLoading(false);
          return;
        }

        // Validate PIN
        if (!/^\d{5}$/.test(adminPin)) {
          setError('Admin PIN must be exactly 5 digits');
          setLoading(false);
          return;
        }

        // Signup flow (recovers from half-finished signups, and signs in
        // users who already have a fully set-up account)
        const { user, existingAccount } = await registerOrganization(
          email.trim(),
          password,
          orgName.trim(),
          adminPin
        );

        if (existingAccount) {
          enterApp(await hasActiveAccess(user.uid));
        } else {
          navigate('/billing');
        }
      } else {
        // Login flow
        const user = await signIn(email.trim(), password);

        // The reads are independent (a missing org has no subscription doc),
        // so fetch them together to halve the post-login wait.
        const [orgExists, hasAccess] = await Promise.all([
          checkOrganizationExists(user.uid),
          hasActiveAccess(user.uid),
        ]);

        // A signup that failed halfway leaves an auth account with no
        // organization — finish that setup here instead of dead-ending
        // on the billing page.
        if (!orgExists) {
          setPendingUser(user);
          setModalOrgName('');
          setShowOrgNameModal(true);
          return;
        }

        enterApp(hasAccess);
      }
    } catch (err) {
      console.error(err);
      setError(
        err instanceof SignupError
          ? err.message
          : getAuthErrorMessage(err, isSignup ? 'signup' : 'signin')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetMessage('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Enter your email above, then tap “Forgot password?”');
      return;
    }

    // One message for every outcome so it never reveals whether an
    // email has an account.
    const sentMessage = `If an account exists for ${trimmedEmail}, we’ve emailed a password reset link.`;

    setLoading(true);
    try {
      await resetPassword(trimmedEmail);
      setResetMessage(sentMessage);
    } catch (err) {
      if (getAuthErrorCode(err) === 'auth/user-not-found') {
        setResetMessage(sentMessage);
      } else {
        console.error(err);
        setError(getAuthErrorMessage(err, 'reset'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const user = await signInWithGoogle();

      const [orgExists, hasAccess] = await Promise.all([
        checkOrganizationExists(user.uid),
        hasActiveAccess(user.uid),
      ]);

      if (!orgExists) {
        // New user - need to collect organization name
        setPendingUser(user);
        setModalOrgName(user.displayName || '');
        setShowOrgNameModal(true);
        setLoading(false);
        return;
      }

      enterApp(hasAccess);
    } catch (err) {
      console.error(err);
      // Closing the popup isn't an error worth alarming anyone about
      const code = getAuthErrorCode(err);
      if (
        code !== 'auth/popup-closed-by-user' &&
        code !== 'auth/cancelled-popup-request'
      ) {
        setError(getAuthErrorMessage(err, 'signin'));
      }
      setLoading(false);
    }
  };

  const handleModalCancel = async () => {
    // A stray backdrop tap while org creation is committing must not sign
    // the user out mid-write
    if (loading) return;

    setShowOrgNameModal(false);
    setPendingUser(null);
    setError('');
    // The account is authenticated but has no organization yet. Leaving it
    // signed in strands the user on a billing page that can never load —
    // sign out so they restart cleanly.
    try {
      await signOut();
    } catch (err) {
      console.error('Error signing out after cancelled setup:', err);
    }
  };

  const handleOrgNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!pendingUser || !modalOrgName.trim()) {
        setError('Please enter an organization name.');
        setLoading(false);
        return;
      }

      // Validate PIN
      if (!/^\d{5}$/.test(modalAdminPin)) {
        setError('Admin PIN must be exactly 5 digits');
        setLoading(false);
        return;
      }

      // Create organization
      await createOrganization(
        pendingUser.uid,
        modalOrgName.trim(),
        pendingUser.email!,
        modalAdminPin
      );

      setShowOrgNameModal(false);
      navigate('/billing');
    } catch (err) {
      console.error(err);
      setError(
        'We couldn’t finish setting up your organization. Please tap Continue to try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="nav-container">
          <img
            src="/assets/images/logo-long-blue.png"
            alt="Sunday Best"
            className="logo"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          <div className="nav-links">
            <button onClick={() => navigate('/')} className="nav-link">
              Home
            </button>
            <button
              onClick={() => navigate('/login')}
              className="nav-link-login"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      <div className={`flip-container ${isSignup ? 'flipped' : ''}`}>
        <div className="flipper">
          {/* Login Side */}
          <div className="login-container front">
            <div className="login-header">
              <h2 className="login-title">Welcome Back! 👋</h2>
              <p className="login-subtitle">
                Sign in to manage your photo booth
              </p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                  placeholder="you@organization.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}
              {resetMessage && (
                <div className="reset-message">{resetMessage}</div>
              )}

              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="divider">
                <span>or</span>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="btn-google"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>
            </form>

            <p className="login-footer">
              Don't have an account?{' '}
              <button onClick={toggleMode} className="signup-link">
                Sign up
              </button>
            </p>

            <Link to="/" className="back-home">
              ← Back to Home
            </Link>
          </div>

          {/* Signup Side */}
          <div className="login-container back">
            <div className="login-header">
              <h2 className="login-title">Create Account 🎉</h2>
              <p className="login-subtitle">
                Join us and start capturing moments
              </p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label className="form-label">Organization Name</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  className="form-input"
                  placeholder="Your Organization"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin PIN (5 digits)</label>
                <input
                  type="text"
                  value={adminPin}
                  onChange={(e) =>
                    setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 5))
                  }
                  required
                  maxLength={5}
                  className="form-input"
                  placeholder="12345"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                  placeholder="you@organization.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="form-input"
                  placeholder="••••••••"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating account...' : 'Create Account'}
              </button>

              <div className="divider">
                <span>or</span>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="btn-google"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign up with Google
              </button>
            </form>

            <p className="login-footer">
              Already have an account?{' '}
              <button onClick={toggleMode} className="signup-link">
                Sign in
              </button>
            </p>

            <Link to="/" className="back-home">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Organization Name Modal */}
      {showOrgNameModal && (
        <div className="modal-overlay" onClick={handleModalCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Welcome! 🎉</h2>
            <p className="modal-subtitle">Set up your organization</p>

            <form onSubmit={handleOrgNameSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">Organization Name</label>
                <input
                  type="text"
                  value={modalOrgName}
                  onChange={(e) => setModalOrgName(e.target.value)}
                  required
                  className="form-input"
                  placeholder="Your Organization"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin PIN (5 digits)</label>
                <input
                  type="text"
                  value={modalAdminPin}
                  onChange={(e) =>
                    setModalAdminPin(
                      e.target.value.replace(/\D/g, '').slice(0, 5)
                    )
                  }
                  required
                  maxLength={5}
                  className="form-input"
                  placeholder="12345"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={handleModalCancel}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
